/**
 * **Where the seed put each plan visit.** The tenant's visits come from the
 * schedule (`seed/schedule.ts`), which places each plan visit on a clinician's
 * real shift. The store dates its plan visits where the schedule placed them,
 * so each one binds to the tenant's visit by construction rather than by luck.
 *
 * The schedule depends only on the demo day, not on the generation time (that
 * moves statuses, never days or patients), so one is built per demo day and kept.
 */
import { createSchedule, type Schedule } from "@caredna-marlin/utils/homeHealthDemo/seed/schedule";
import { dateKeyAt } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { DEFAULT_CONFIG } from "@caredna-marlin/utils/homeHealthDemo/fixtures/config";
import type { DateKey } from "@caredna-marlin/utils/homeHealthDemo/state";

const schedules = new Map<DateKey, Schedule>();
const days = new Map<DateKey, Map<string, DateKey>>();

/** The schedule the seed writes for this demo day. */
export function scheduleFor(demoToday: DateKey): Schedule {
  const hit = schedules.get(demoToday);
  if (hit) return hit;
  const schedule = createSchedule(demoToday, dateKeyAt(demoToday, "08:00", DEFAULT_CONFIG.branch.timeZone));
  schedules.set(demoToday, schedule);
  return schedule;
}

/** Plan visit id → the day the seed placed it on. */
export function scheduledDays(demoToday: DateKey): Map<string, DateKey> {
  const hit = days.get(demoToday);
  if (hit) return hit;
  const out = new Map<string, DateKey>();
  for (const v of scheduleFor(demoToday).visits) if (v.planVisitId) out.set(v.planVisitId, v.day);
  days.set(demoToday, out);
  return out;
}
